/**
 * Rows that would have left, held in place because somebody is on them.
 *
 * An edit can make a row stop matching the view's filters, or belong at a
 * different index. Applying that straight away is correct and unusable: the row
 * being typed into disappears from under the cursor, or jumps forty rows up
 * mid-keystroke, and the next character goes somewhere else.
 *
 * So the values are applied immediately -- what was typed is what is shown --
 * and the *departure* is deferred until the selection leaves the row. Until
 * then the row carries a badge saying why it is about to go, because a row that
 * silently disagrees with the filters above it is worse than one that says so.
 *
 * The decision is answered here, away from React and away from the store, so
 * that "should this row be held, and what should it say" is testable against
 * plain values.
 */
import type { WindowOperation } from './rowEvents'

/** Why a held row is not where the view says it belongs. */
export type HoldReason = 'filters' | 'sort'

export interface HeldRow {
  reason: HoldReason
  /** What to do to the window once the row is let go. */
  deferred: readonly WindowOperation[]
}

/**
 * Splits an edit's operations into what to apply now and what to defer.
 *
 * The value update always applies: it is what the person typed, and holding it
 * back would make the cell lag the keyboard. A `remove` or a `move` is the
 * departure, and that is what waits.
 *
 * Returns no hold when there is nothing to defer, so a caller can treat "held"
 * and "applied" the same way and let the absence of a reason mean neither.
 */
export function splitHold(operations: readonly WindowOperation[]): {
  now: readonly WindowOperation[]
  hold: HeldRow | null
} {
  const departures = operations.filter(
    (operation) => operation.type === 'remove' || operation.type === 'move',
  )

  if (departures.length === 0) return { now: operations, hold: null }

  return {
    now: operations.filter(
      (operation) => operation.type !== 'remove' && operation.type !== 'move',
    ),
    hold: {
      // A row that no longer matches is leaving the view; one that only moved
      // is staying and sitting in the wrong place. The first is the stronger
      // statement, so it wins when an edit somehow does both.
      reason: departures.some((operation) => operation.type === 'remove')
        ? 'filters'
        : 'sort',
      deferred: departures,
    },
  }
}

/**
 * Which held rows the selection has left.
 *
 * Everything not under the selection any more, which is the whole rule: a row
 * is held because somebody is on it, so it is let go the moment they are not.
 */
export function releasableRows(
  held: ReadonlyMap<number, HeldRow>,
  selectedRowIds: ReadonlySet<number>,
): number[] {
  return [...held.keys()].filter((rowId) => !selectedRowIds.has(rowId))
}
